import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Pagination from '../../components/common/Pagination';
import { getFullImageUrl, fetchETLStatus, fetchETLSyncHistory, triggerETLSync, fetchDBConnections, fetchLoginLogs, fetchSystemSettings, saveSystemSettings } from '../../services/api';
import { useSystemSettings } from '../../context/SystemSettingsContext';
import './SystemManagementPage.css';

function SystemManagementPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { refreshSettings } = useSystemSettings();
    const [activeMenu, setActiveMenu] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('tab') || 'general';
    });
    const [loading, setLoading] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);

    // ETL 상태 관련 state
    const [etlStatus, setEtlStatus] = useState(null);
    const [etlHistory, setEtlHistory] = useState([]);
    const [dbConnections, setDbConnections] = useState(null);
    const [etlLoading, setEtlLoading] = useState(false);
    const [syncTriggering, setSyncTriggering] = useState(false);

    // 로그인 로그 관련 state
    const [loginLogs, setLoginLogs] = useState([]);
    const [loginLogsLoading, setLoginLogsLoading] = useState(false);
    const [logFilter, setLogFilter] = useState(''); // '', 'LOGIN', 'LOGOUT', 'LOGIN_FAILED'

    // ETL 페이지네이션 상태
    const [etlPage, setEtlPage] = useState(0);
    const [etlSize, setEtlSize] = useState(10);

    // 로그 페이지네이션 상태
    const [logPage, setLogPage] = useState(0);
    const [logSize, setLogSize] = useState(10);

    // URL 파라미터에서 탭 정보 가져오기
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab) {
            setActiveMenu(tab);
        }
    }, [location.search]);

    const [formData, setFormData] = useState({
        systemName: '사출 통합 관제 시스템',
        companyName: '',
        companyCode: '',
        schemaName: '',
        businessNumber: '',
        representativeName: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        logoUrl: '',
        faviconUrl: '',
        refreshInterval: '30초',
        timezone: 'Asia/Seoul',
        // 보안 설정
        maxLoginAttempts: 5,
        lockDurationMinutes: 30,
        // 알림 설정
        notifyWeb: true,
        notifyEmail: false,
        notifySms: false,
        notifyPush: false
    });

    // 시스템 설정 조회 (데모용 - mock API 사용)
    useEffect(() => {
        const loadSystemSettings = async () => {
            try {
                setLoading(true);
                const result = await fetchSystemSettings();
                if (result.success) {
                    const data = result.data;
                    setFormData({
                        systemName: data.systemName || '사출 통합 관제 시스템',
                        companyName: data.companyName || '(주)서로',
                        companyCode: data.companyCode || 'SEORO',
                        schemaName: data.schemaName || 'seoro',
                        businessNumber: data.businessNumber || '123-45-67890',
                        representativeName: data.representativeName || '김대표',
                        contactEmail: data.contactEmail || 'ceo@seoro.ai.kr',
                        contactPhone: data.contactPhone || '032-721-7118',
                        address: data.address || '인천광역시 연수구 송도미래로11번길27, 스마트스퀘어 A동 1501호',
                        logoUrl: data.logoUrl || '',
                        faviconUrl: data.faviconUrl || '',
                        refreshInterval: data.refreshInterval || '30초',
                        timezone: data.timezone || 'Asia/Seoul',
                        // 보안 설정
                        maxLoginAttempts: data.maxLoginAttempts || 5,
                        lockDurationMinutes: data.lockDurationMinutes || 30,
                        // 알림 설정
                        notifyWeb: data.notifyWeb !== undefined ? data.notifyWeb : true,
                        notifyEmail: data.notifyEmail !== undefined ? data.notifyEmail : false,
                        notifySms: data.notifySms !== undefined ? data.notifySms : false,
                        notifyPush: data.notifyPush !== undefined ? data.notifyPush : false
                    });
                }
            } catch (error) {
                console.error('시스템 설정 조회 실패:', error);
            } finally {
                setLoading(false);
            }
        };

        loadSystemSettings();
    }, []);

    // ETL 데이터 로드 함수
    const loadETLData = useCallback(async () => {
        setEtlLoading(true);
        try {
            const [statusResult, historyResult, dbResult] = await Promise.allSettled([
                fetchETLStatus(),
                fetchETLSyncHistory(100),
                fetchDBConnections()
            ]);

            if (statusResult.status === 'fulfilled') {
                setEtlStatus(statusResult.value);
            }
            if (historyResult.status === 'fulfilled') {
                setEtlHistory(historyResult.value || []);
            }
            if (dbResult.status === 'fulfilled') {
                setDbConnections(dbResult.value);
            }
        } catch (error) {
            console.error('ETL 데이터 로드 실패:', error);
        } finally {
            setEtlLoading(false);
        }
    }, []);

    // 데이터 수집관리 탭 선택시 ETL 데이터 로드
    useEffect(() => {
        if (activeMenu === 'data-collection') {
            loadETLData();
        }
    }, [activeMenu, loadETLData]);

    // ETL 수동 실행 핸들러
    const handleTriggerSync = async () => {
        setSyncTriggering(true);
        try {
            await triggerETLSync();
            setSaveMessage('ETL 동기화가 시작되었습니다.');
            setTimeout(() => setSaveMessage(''), 3000);
            // 3초 후 상태 새로고침
            setTimeout(() => loadETLData(), 3000);
        } catch (error) {
            console.error('ETL 동기화 실행 실패:', error);
            setSaveMessage('ETL 동기화 실행에 실패했습니다.');
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setSyncTriggering(false);
        }
    };

    // 로그인 로그 로드 함수
    const loadLoginLogs = useCallback(async (eventType = '') => {
        setLoginLogsLoading(true);
        try {
            const logs = await fetchLoginLogs(0, 50, eventType || null);
            setLoginLogs(logs || []);
        } catch (error) {
            console.error('로그인 로그 로드 실패:', error);
            setLoginLogs([]);
        } finally {
            setLoginLogsLoading(false);
        }
    }, []);

    // 시스템로그 탭 선택시 로그인 로그 로드
    useEffect(() => {
        if (activeMenu === 'log') {
            loadLoginLogs(logFilter);
        }
    }, [activeMenu, logFilter, loadLoginLogs]);

    // 로그 필터 변경 핸들러
    const handleLogFilterChange = (filter) => {
        setLogFilter(filter);
        setLogPage(0);
    };

    // ETL 페이지네이션 적용
    const paginatedEtlHistory = useMemo(() => {
        const startIndex = etlPage * etlSize;
        return etlHistory.slice(startIndex, startIndex + etlSize);
    }, [etlHistory, etlPage, etlSize]);

    const etlTotalElements = etlHistory.length;
    const etlTotalPages = Math.ceil(etlTotalElements / etlSize);

    const handleEtlPageChange = (newPage) => {
        setEtlPage(newPage);
    };

    const handleEtlSizeChange = (newSize) => {
        setEtlSize(newSize);
        setEtlPage(0);
    };

    // 로그 페이지네이션 적용
    const paginatedLogs = useMemo(() => {
        const startIndex = logPage * logSize;
        return loginLogs.slice(startIndex, startIndex + logSize);
    }, [loginLogs, logPage, logSize]);

    const logTotalElements = loginLogs.length;
    const logTotalPages = Math.ceil(logTotalElements / logSize);

    const handleLogPageChange = (newPage) => {
        setLogPage(newPage);
    };

    const handleLogSizeChange = (newSize) => {
        setLogSize(newSize);
        setLogPage(0);
    };

    // 이벤트 타입 한글 변환
    const getEventTypeLabel = (eventType) => {
        switch (eventType) {
            case 'LOGIN': return '로그인';
            case 'LOGOUT': return '로그아웃';
            case 'LOGIN_FAILED': return '로그인 실패';
            default: return eventType;
        }
    };

    // 이벤트 타입별 스타일 클래스
    const getEventTypeClass = (eventType) => {
        switch (eventType) {
            case 'LOGIN': return 'success';
            case 'LOGOUT': return 'info';
            case 'LOGIN_FAILED': return 'error';
            default: return '';
        }
    };

    // 시간 포맷팅 함수
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const menuItems = [
        { id: 'general', label: '일반설정', icon: 'settings' },
        { id: 'monitoring', label: '모니터링설정', icon: 'monitor' },
        { id: 'alarm', label: '알림설정', icon: 'notifications' },
        { id: 'security', label: '보안설정', icon: 'security' },
        { id: 'data-collection', label: '데이터 수집관리', icon: 'sync' },
        { id: 'log', label: '시스템로그', icon: 'description' },
        { id: 'ip', label: 'IP관리', icon: 'lan', path: '/system/ip' }
    ];

    // 탭 변경 핸들러
    const handleMenuChange = (menuId) => {
        const menuItem = menuItems.find(item => item.id === menuId);
        if (menuItem?.path) {
            // 별도 페이지로 이동
            navigate(menuItem.path);
        } else {
            setActiveMenu(menuId);
            navigate(`/system?tab=${menuId}`);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 알림 토글 변경 핸들러 (즉시 저장)
    const handleNotifyToggle = async (field, value) => {
        // UI 먼저 업데이트
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // 데모용 - 항상 성공으로 처리
        try {
            await saveSystemSettings({ [field]: value });
            // 성공 - 아무것도 안 함 (이미 상태 업데이트됨)
        } catch (error) {
            console.error('알림 설정 저장 실패:', error);
            // 실패 시 롤백
            setFormData(prev => ({
                ...prev,
                [field]: !value
            }));
        }
    };

    // 파일 업로드 핸들러 (데모용 - base64 미리보기만)
    const handleFileUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
        setUploading(true);

        const fieldName = type === 'logo' ? 'logoUrl' : 'faviconUrl';
        const previewFieldName = `${fieldName}Preview`;

        // 로컬 미리보기를 위해 FileReader 사용 (base64)
        const base64Promise = new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                resolve(event.target.result);
            };
            reader.readAsDataURL(file);
        });

        try {
            // base64 미리보기 설정 (데모에서는 실제 업로드 안 함)
            const base64Data = await base64Promise;
            setFormData(prev => ({
                ...prev,
                [previewFieldName]: base64Data,
                [fieldName]: base64Data // 데모에서는 base64를 URL로 사용
            }));

            setSaveMessage(`${type === 'logo' ? '로고' : '파비콘'} 업로드 완료 (데모)`);
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (error) {
            console.error('파일 업로드 실패:', error);
            setSaveMessage('파일 업로드에 실패했습니다.');
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setUploading(false);
            e.target.value = ''; // 같은 파일 다시 선택 가능하도록
        }
    };

    // 파일 삭제 핸들러 (UI에서만 삭제 표시, 실제 삭제는 저장 시)
    const handleFileDelete = (type) => {
        const fieldName = type === 'logo' ? 'logoUrl' : 'faviconUrl';
        const previewFieldName = `${fieldName}Preview`;
        const pendingDeleteField = `${fieldName}PendingDelete`;

        // 삭제 예정으로 표시하고 UI에서 숨김
        setFormData(prev => ({
            ...prev,
            [pendingDeleteField]: prev[fieldName], // 삭제 예정 URL 저장
            [fieldName]: '',
            [previewFieldName]: ''
        }));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setSaveMessage('');

            // 데모용 - 항상 성공
            const result = await saveSystemSettings(formData);
            if (result.success) {
                // 삭제 예정 상태 초기화
                setFormData(prev => ({
                    ...prev,
                    logoUrlPendingDelete: '',
                    faviconUrlPendingDelete: ''
                }));
                setSaveMessage('설정이 저장되었습니다 (데모).');
                setTimeout(() => setSaveMessage(''), 3000);
            } else {
                setSaveMessage('설정 저장에 실패했습니다.');
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (error) {
            console.error('설정 저장 실패:', error);
            setSaveMessage('설정 저장에 실패했습니다.');
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        switch (activeMenu) {
            case 'general':
                return (
                    <div className="system-settings-content">
                        <h2 className="settings-title">일반설정</h2>
                        <div className="settings-form">
                            {/* 기본 정보 섹션 */}
                            <div className="form-section">
                                <h3 className="form-section-title">기본 정보</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">시스템명</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.systemName}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">회사명</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.companyName}
                                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">회사코드</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.companyCode}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">스키마명</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.schemaName}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">사업자등록번호</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.businessNumber}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">대표자명</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.representativeName}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 연락처 정보 섹션 */}
                            <div className="form-section">
                                <h3 className="form-section-title">연락처 정보</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">대표 이메일</label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            value={formData.contactEmail}
                                            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                                            placeholder="example@company.com"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">대표 연락처</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.contactPhone}
                                            onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                                            placeholder="02-1234-5678"
                                        />
                                    </div>
                                </div>
                                <div className="form-row single">
                                    <div className="form-group full-width">
                                        <label className="form-label">주소</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.address}
                                            onChange={(e) => handleInputChange('address', e.target.value)}
                                            placeholder="서울시 강남구 테헤란로 123"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 브랜딩 정보 섹션 */}
                            <div className="form-section">
                                <h3 className="form-section-title">브랜딩</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">로고</label>
                                        <div className="file-upload-container">
                                            {(formData.logoUrlPreview || formData.logoUrl) && (
                                                <div className="image-preview">
                                                    <img src={formData.logoUrlPreview || getFullImageUrl(formData.logoUrl)} alt="로고 미리보기" />
                                                </div>
                                            )}
                                            <div className="file-upload-actions">
                                                <input
                                                    type="file"
                                                    id="logo-upload"
                                                    accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => handleFileUpload(e, 'logo')}
                                                />
                                                <label htmlFor="logo-upload" className="file-upload-btn">
                                                    <span className="material-symbols-outlined">upload</span>
                                                    {formData.logoUrl ? '변경' : '업로드'}
                                                </label>
                                                {formData.logoUrl && (
                                                    <button
                                                        type="button"
                                                        className="file-delete-btn"
                                                        onClick={() => handleFileDelete('logo')}
                                                    >
                                                        <span className="material-symbols-outlined">delete</span>
                                                        삭제
                                                    </button>
                                                )}
                                            </div>
                                            {uploadingLogo && <span className="upload-status">업로드 중...</span>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">파비콘</label>
                                        <div className="file-upload-container">
                                            {(formData.faviconUrlPreview || formData.faviconUrl) && (
                                                <div className="image-preview favicon-preview">
                                                    <img src={formData.faviconUrlPreview || getFullImageUrl(formData.faviconUrl)} alt="파비콘 미리보기" />
                                                </div>
                                            )}
                                            <div className="file-upload-actions">
                                                <input
                                                    type="file"
                                                    id="favicon-upload"
                                                    accept="image/png,image/jpeg,image/jpg,image/gif,image/ico,image/x-icon,image/svg+xml"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => handleFileUpload(e, 'favicon')}
                                                />
                                                <label htmlFor="favicon-upload" className="file-upload-btn">
                                                    <span className="material-symbols-outlined">upload</span>
                                                    {formData.faviconUrl ? '변경' : '업로드'}
                                                </label>
                                                {formData.faviconUrl && (
                                                    <button
                                                        type="button"
                                                        className="file-delete-btn"
                                                        onClick={() => handleFileDelete('favicon')}
                                                    >
                                                        <span className="material-symbols-outlined">delete</span>
                                                        삭제
                                                    </button>
                                                )}
                                            </div>
                                            {uploadingFavicon && <span className="upload-status">업로드 중...</span>}
                                        </div>
                                    </div>
                                </div>
                                <p className="form-hint">지원 형식: JPG, PNG, GIF, SVG, ICO (최대 5MB)</p>
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessage.includes('실패') ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button className="save-btn" onClick={handleSave} disabled={loading}>
                                    {loading ? '저장 중...' : '설정 저장'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'monitoring':
                return (
                    <div className="system-settings-content">
                        <h2 className="settings-title">모니터링설정</h2>
                        <div className="settings-form">
                            {/* 대시보드 설정 섹션 */}
                            <div className="form-section">
                                <h3 className="form-section-title">대시보드 설정</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">자동 새로고침 간격</label>
                                        <div className="select-container">
                                            <select
                                                className="form-select"
                                                value={formData.refreshInterval || '30초'}
                                                onChange={(e) => handleInputChange('refreshInterval', e.target.value)}
                                            >
                                                <option value="수집완료시">데이터 수집완료 시</option>
                                                <option value="10초">10초</option>
                                                <option value="30초">30초</option>
                                                <option value="1분">1분</option>
                                                <option value="5분">5분</option>
                                                <option value="10분">10분</option>
                                                <option value="사용안함">사용안함</option>
                                            </select>
                                            <svg className="select-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">기준 시간</label>
                                        <div className="select-container">
                                            <select
                                                className="form-select"
                                                value={formData.timezone || 'Asia/Seoul'}
                                                onChange={(e) => handleInputChange('timezone', e.target.value)}
                                            >
                                                <option value="Asia/Seoul">한국 표준시 (KST, UTC+9)</option>
                                                <option value="Asia/Ho_Chi_Minh">베트남 표준시 (ICT, UTC+7)</option>
                                                <option value="UTC">협정 세계시 (UTC)</option>
                                                <option value="Asia/Tokyo">일본 표준시 (JST, UTC+9)</option>
                                                <option value="America/New_York">미국 동부시 (EST/EDT)</option>
                                                <option value="Europe/London">영국 표준시 (GMT/BST)</option>
                                            </select>
                                            <svg className="select-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessage.includes('실패') ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button className="save-btn" onClick={handleSave} disabled={loading}>
                                    {loading ? '저장 중...' : '설정 저장'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'alarm':
                return (
                    <div className="system-settings-content">
                        <h2 className="settings-title">알림설정</h2>
                        <div className="settings-form">
                            {/* 알림 채널 설정 */}
                            <div className="form-section">
                                <h3 className="form-section-title">알림 채널</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">웹 상단 알림</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.notifyWeb}
                                                    onChange={(e) => handleNotifyToggle('notifyWeb', e.target.checked)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{formData.notifyWeb ? 'ON' : 'OFF'}</span>
                                        </div>
                                        <span className="form-hint">웹 상단에 알림 메시지를 표시합니다</span>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">이메일 알림</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch disabled">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.notifyEmail}
                                                    disabled
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{formData.notifyEmail ? 'ON' : 'OFF'}</span>
                                        </div>
                                        <span className="form-hint">등록된 이메일로 알림을 발송합니다 (연동 예정)</span>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">SMS 알림</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch disabled">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.notifySms}
                                                    disabled
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{formData.notifySms ? 'ON' : 'OFF'}</span>
                                        </div>
                                        <span className="form-hint">등록된 휴대폰으로 문자를 발송합니다 (연동 예정)</span>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">웹 푸시 알림</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch disabled">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.notifyPush}
                                                    disabled
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{formData.notifyPush ? 'ON' : 'OFF'}</span>
                                        </div>
                                        <span className="form-hint">브라우저 푸시 알림을 발송합니다 (연동 예정)</span>
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessage.includes('실패') ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button className="save-btn" onClick={handleSave} disabled={loading}>
                                    {loading ? '저장 중...' : '설정 저장'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'security':
                return (
                    <div className="system-settings-content">
                        <h2 className="settings-title">보안설정</h2>
                        <div className="settings-form">
                            {/* 로그인 보안 설정 */}
                            <div className="form-section">
                                <h3 className="form-section-title">로그인 보안</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">최대 로그인 실패 횟수</label>
                                        <div className="select-container">
                                            <select
                                                className="form-select"
                                                value={formData.maxLoginAttempts}
                                                onChange={(e) => handleInputChange('maxLoginAttempts', parseInt(e.target.value))}
                                            >
                                                <option value={3}>3회</option>
                                                <option value={5}>5회</option>
                                                <option value={10}>10회</option>
                                            </select>
                                            <svg className="select-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <span className="form-hint">설정된 횟수만큼 로그인 실패 시 계정이 잠깁니다</span>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">계정 잠금 시간</label>
                                        <div className="select-container">
                                            <select
                                                className="form-select"
                                                value={formData.lockDurationMinutes}
                                                onChange={(e) => handleInputChange('lockDurationMinutes', parseInt(e.target.value))}
                                            >
                                                <option value={5}>5분</option>
                                                <option value={10}>10분</option>
                                                <option value={30}>30분</option>
                                                <option value={60}>1시간</option>
                                                <option value={1440}>24시간</option>
                                            </select>
                                            <svg className="select-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <span className="form-hint">계정 잠금 후 자동 해제까지의 시간</span>
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessage.includes('실패') ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button className="save-btn" onClick={handleSave} disabled={loading}>
                                    {loading ? '저장 중...' : '설정 저장'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'data-collection':
                return (
                    <div className="system-settings-content">
                        <h2 className="settings-title">데이터 수집관리</h2>
                        <div className="settings-form">
                            {/* ETL 현재 상태 */}
                            <div className="form-section">
                                <h3 className="form-section-title">ETL 현재 상태</h3>
                                {etlLoading ? (
                                    <p className="coming-soon">로딩 중...</p>
                                ) : (
                                    <div className="etl-status-grid">
                                        <div className="etl-status-card">
                                            <div className="etl-status-icon">
                                                <span className={`material-symbols-outlined ${etlStatus?.is_running ? 'status-running' : 'status-idle'}`}>
                                                    {etlStatus?.is_running ? 'sync' : 'check_circle'}
                                                </span>
                                            </div>
                                            <div className="etl-status-info">
                                                <span className="etl-status-label">실행 상태</span>
                                                <span className={`etl-status-value ${etlStatus?.is_running ? 'running' : 'idle'}`}>
                                                    {etlStatus?.is_running ? '실행 중' : '대기'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="etl-status-card">
                                            <div className="etl-status-icon">
                                                <span className="material-symbols-outlined">schedule</span>
                                            </div>
                                            <div className="etl-status-info">
                                                <span className="etl-status-label">마지막 실행</span>
                                                <span className="etl-status-value">{formatDateTime(etlStatus?.last_run)}</span>
                                            </div>
                                        </div>
                                        <div className="etl-status-card">
                                            <div className="etl-status-icon">
                                                <span className="material-symbols-outlined status-success">verified</span>
                                            </div>
                                            <div className="etl-status-info">
                                                <span className="etl-status-label">마지막 성공</span>
                                                <span className="etl-status-value">{formatDateTime(etlStatus?.last_success)}</span>
                                            </div>
                                        </div>
                                        <div className="etl-status-card">
                                            <div className="etl-status-icon">
                                                <span className="material-symbols-outlined">database</span>
                                            </div>
                                            <div className="etl-status-info">
                                                <span className="etl-status-label">처리 레코드</span>
                                                <span className="etl-status-value">{etlStatus?.records_processed?.toLocaleString() || 0}건</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* DB 연결 상태 */}
                            <div className="form-section">
                                <h3 className="form-section-title">데이터베이스 연결 상태</h3>
                                <div className="db-connection-grid">
                                    <div className={`db-connection-card ${dbConnections?.databases?.raw_db?.connected ? 'connected' : 'disconnected'}`}>
                                        <div className="db-icon">
                                            <span className="material-symbols-outlined">
                                                {dbConnections?.databases?.raw_db?.connected ? 'cloud_done' : 'cloud_off'}
                                            </span>
                                        </div>
                                        <div className="db-info">
                                            <span className="db-name">Raw DB (MariaDB)</span>
                                            <span className="db-status">
                                                {dbConnections?.databases?.raw_db?.connected ? '연결됨' : '연결 실패'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={`db-connection-card ${dbConnections?.databases?.monitoring_db?.connected ? 'connected' : 'disconnected'}`}>
                                        <div className="db-icon">
                                            <span className="material-symbols-outlined">
                                                {dbConnections?.databases?.monitoring_db?.connected ? 'cloud_done' : 'cloud_off'}
                                            </span>
                                        </div>
                                        <div className="db-info">
                                            <span className="db-name">Monitoring DB (PostgreSQL)</span>
                                            <span className="db-status">
                                                {dbConnections?.databases?.monitoring_db?.connected ? '연결됨' : '연결 실패'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ETL 동기화 이력 */}
                            <div className="form-section no-padding-bottom">
                                <h3 className="form-section-title">동기화 이력</h3>
                                <div className="etl-history-table-container">
                                    <table className="etl-history-table">
                                        <thead>
                                            <tr>
                                                <th>동기화 시간</th>
                                                <th>상태</th>
                                                <th>처리 레코드</th>
                                                <th>대상 테이블</th>
                                                <th>비고</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedEtlHistory.length > 0 ? (
                                                paginatedEtlHistory.map((item, index) => (
                                                    <tr key={item.id || index}>
                                                        <td>{formatDateTime(item.sync_time)}</td>
                                                        <td>
                                                            <span className={`status-badge ${item.sync_status?.toLowerCase()}`}>
                                                                {item.sync_status === 'SUCCESS' ? '성공' : '실패'}
                                                            </span>
                                                        </td>
                                                        <td>{item.total_records?.toLocaleString() || 0}건</td>
                                                        <td className="table-list">{item.table_list || '-'}</td>
                                                        <td className="error-message">{item.error_message || '-'}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="no-data">동기화 이력이 없습니다</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                {etlTotalElements > 0 && (
                                    <Pagination
                                        page={etlPage}
                                        totalPages={etlTotalPages}
                                        totalElements={etlTotalElements}
                                        size={etlSize}
                                        onPageChange={handleEtlPageChange}
                                        onSizeChange={handleEtlSizeChange}
                                    />
                                )}
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessage.includes('실패') ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button
                                    className="save-btn"
                                    onClick={loadETLData}
                                    disabled={etlLoading}
                                    style={{ marginRight: '8px', background: '#4B5563' }}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '4px' }}>refresh</span>
                                    새로고침
                                </button>
                                <button
                                    className="save-btn"
                                    onClick={handleTriggerSync}
                                    disabled={syncTriggering}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '4px' }}>sync</span>
                                    {syncTriggering ? '실행 중...' : etlStatus?.is_running ? '동기화 진행중' : '수동 동기화'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'log':
                return (
                    <div className="system-settings-content">
                        <h2 className="settings-title">시스템로그</h2>
                        <div className="settings-form">
                            {/* 필터 버튼 */}
                            <div className="form-section">
                                <h3 className="form-section-title">로그인/로그아웃 이력</h3>
                                <div className="log-filter-buttons">
                                    <button
                                        className={`filter-btn ${logFilter === '' ? 'active' : ''}`}
                                        onClick={() => handleLogFilterChange('')}
                                    >
                                        전체
                                    </button>
                                    <button
                                        className={`filter-btn ${logFilter === 'LOGIN' ? 'active' : ''}`}
                                        onClick={() => handleLogFilterChange('LOGIN')}
                                    >
                                        로그인
                                    </button>
                                    <button
                                        className={`filter-btn ${logFilter === 'LOGOUT' ? 'active' : ''}`}
                                        onClick={() => handleLogFilterChange('LOGOUT')}
                                    >
                                        로그아웃
                                    </button>
                                    <button
                                        className={`filter-btn ${logFilter === 'LOGIN_FAILED' ? 'active' : ''}`}
                                        onClick={() => handleLogFilterChange('LOGIN_FAILED')}
                                    >
                                        로그인 실패
                                    </button>
                                </div>
                            </div>

                            {/* 로그 테이블 */}
                            <div className="form-section no-padding-bottom">
                                <div className="login-log-table-wrapper">
                                    {loginLogsLoading ? (
                                        <p className="coming-soon" style={{ padding: '40px', textAlign: 'center' }}>로딩 중...</p>
                                    ) : (
                                        <table className="etl-history-table login-log-table">
                                            <thead>
                                                <tr>
                                                    <th>시간</th>
                                                    <th>유형</th>
                                                    <th>사용자</th>
                                                    <th>이메일</th>
                                                    <th>IP 주소</th>
                                                    <th>비고</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedLogs.length > 0 ? (
                                                    paginatedLogs.map((log, index) => (
                                                        <tr key={log.id || index}>
                                                            <td>{formatDateTime(log.createdAt)}</td>
                                                            <td>
                                                                <span className={`status-badge ${getEventTypeClass(log.eventType)}`}>
                                                                    {getEventTypeLabel(log.eventType)}
                                                                </span>
                                                            </td>
                                                            <td>{log.username || '-'}</td>
                                                            <td>{log.email || '-'}</td>
                                                            <td>{log.ipAddress || '-'}</td>
                                                            <td className="error-message">
                                                                {log.eventType === 'LOGIN_FAILED' ? log.failureReason : '-'}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="no-data">로그인 이력이 없습니다</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                {logTotalElements > 0 && (
                                    <Pagination
                                        page={logPage}
                                        totalPages={logTotalPages}
                                        totalElements={logTotalElements}
                                        size={logSize}
                                        onPageChange={handleLogPageChange}
                                        onSizeChange={handleLogSizeChange}
                                    />
                                )}
                            </div>

                            <div className="form-actions">
                                <button
                                    className="save-btn"
                                    onClick={() => loadLoginLogs(logFilter)}
                                    disabled={loginLogsLoading}
                                    style={{ background: '#4B5563' }}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '4px' }}>refresh</span>
                                    새로고침
                                </button>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <MainLayout>
            <div className="system-management-container">
                {/* Left Sidebar */}
                <div className="system-sidebar">
                    <div className="sidebar-header">
                        <h1 className="sidebar-title">시스템관리</h1>
                        <p className="sidebar-subtitle">시스템 설정 및 관리 기능</p>
                    </div>
                    <nav className="sidebar-nav">
                        {menuItems.map((item) => (
                            <div
                                key={item.id}
                                className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
                                onClick={() => handleMenuChange(item.id)}
                            >
                                <span className="material-symbols-outlined nav-icon">{item.icon}</span>
                                <span className="nav-label">{item.label}</span>
                            </div>
                        ))}
                    </nav>
                </div>

                {/* Main Content */}
                <div className="system-main-content">
                    {renderContent()}
                </div>
            </div>
        </MainLayout>
    );
}

export default SystemManagementPage;

